import { DEFAULT_PARTNER } from "@pandas/core-ui";
import { servicesOrchestratorValues } from "@/config/services";
import { buildQueryString, type SearchParamsInput, type SearchParamsSource } from "@/utils/searchParams";

type SearchLike = SearchParamsInput | SearchParamsSource;

const toAbsoluteUrl = (value: string) => {
	if (!value) return "";
	return value.startsWith("http://") || value.startsWith("https://") ? value : `https://${value}`;
};

export const buildHref = (pathname: string, search?: SearchLike) => {
	const queryString = search ? buildQueryString(search) : "";
	return queryString ? `${pathname}?${queryString}` : pathname;
};

export const getVisionAppBaseUrl = () => {
	return toAbsoluteUrl(servicesOrchestratorValues.urls.vision) || window.location.origin;
};

export const getFullUrl = (pathname: string, searchParams: string | { toString(): string }) => {
	const url = new URL(pathname, window.origin);
	url.search = typeof searchParams === "string" ? searchParams : searchParams.toString();
	return url.toString();
};

export const getPathNameWithoutLocaleAndPartner = (pathname: string) => {
	// Strip /$locale/$partner prefix and return last segment
	const segments = pathname.split("/").filter(Boolean);
	// Skip locale (first) and partner (second) segments
	const remaining = segments.slice(2);
	return remaining.pop() || "";
};

export const isHomePage = (pathname: string): boolean => {
	return getPathNameWithoutLocaleAndPartner(pathname) === "";
};

export const getWebappUrl = (locale: string, configId: string | null, sessionId: string | null) => {
	return `${servicesOrchestratorValues.urls.webapp}/${locale}/${configId}?sessionId=${sessionId}&flow=vision-ai`;
};

export const getPricingUrl = (locale: string, configId: string, sessionId: string) => {
	return `${servicesOrchestratorValues.urls.pricing}/${locale}/${configId}/smartphone?sessionId=${sessionId}`;
};

export const getPeerUrl = (
	locale: string,
	partnerCode: string | null,
	partnerToken: string | undefined,
	locationId: string | undefined,
	sessionId: string | null,
	extraParams?: Record<string, string | number | boolean | null | undefined>,
) => {
	const baseUrl = getVisionAppBaseUrl();
	const partner = partnerCode || DEFAULT_PARTNER;
	const url = new URL(`/${locale}/${partner}/peer`, baseUrl);

	if (sessionId) url.searchParams.set("sessionId", sessionId);
	if (partnerCode) url.searchParams.set("partnerCode", partnerCode);
	if (partnerToken) url.searchParams.set("partnerToken", partnerToken);
	if (locationId) url.searchParams.set("locationId", locationId);

	if (extraParams) {
		Object.entries(extraParams).forEach(([key, value]) => {
			if (value !== undefined && value !== null) {
				url.searchParams.set(key, String(value));
			}
		});
	}

	return url.toString();
};
